import {create} from "zustand";
import {derivedRemoteStore} from "./DerivedStore";
import {useVisitReportStore} from "./VisitReportStore";
import {useKeyDataStore} from "./KeyDataStore";
import {RemoteValue} from "../util/RemoteValue";
import {KeyData, KeyOfficeVisitReport, OfficeVisit} from "./Model";
import {associateBy, groupBy} from "../util/MapUtil";
import {month} from "../util/DateUtil";
import {useDeskStore} from "./DeskStore";
import {
    calculateCumulativeDistribution,
    calculateKeyVisitCountPerDate,
    calculateVisitStatistics,
    VisitStats
} from "../components/visit-stat-block/OfficeVisitStats";
import {mapNotNull} from "../util/ArrayUtil";
import {stringCmp} from "../util/SortUtil";


type GhostUsage = {
    keyNumber: string,
    keyData: KeyData | undefined,
    usages: ({
        type: 'not-issued',
        date: string,
    } | {
        type: 'no-history',
        date: string,
    } | {
        type: 'ghost',
        locationTypes: ('with-vault' | 'lost' | 'damaged' | 'deactivated')[],
        date: string,
    })[],
};

export type KeyValidation = {
    issuedKeys: KeyData[];
    missingFromReportKeys: KeyData[];
    ghostUsages: GhostUsage[];
    reportedNotIssuedKeys: {
        keyNumber: string,
        usages: number,
    }[],
};

export type MonthlyOfficeVisitReport = KeyOfficeVisitReport & {
    month: string,
    officeVisits: OfficeVisit[],
    visitStats: VisitStats,
    deskCount: number,
    cumulativeVisitDistribution: number[],
    validation: KeyValidation,
};

export type VisitWithStatStore = {
    remoteValue: RemoteValue<MonthlyOfficeVisitReport[]>,
};


function guessMonths(report: KeyOfficeVisitReport): string[] {
    const month2dates = groupBy(
        report.sheet.dates
            .map(date => new Date(date)),
        (date: Date) => month(date),
    );
    return Array.from(month2dates.entries())
        .filter(([, dates]) => dates.length > 2)
        .map(([month]) => month)
}

function findGhostUsages(report: KeyOfficeVisitReport, registeredKeys: Map<string, KeyData>): GhostUsage[] {
    return report.sheet.rows
        .map(row => {
            const keyData = registeredKeys.get(row.value);
            const visitDates = report.sheet.dates
                .map((date, dateIndex) => ({date, dateIndex}))
                .filter(({dateIndex}) => row.visits[dateIndex]);
            if (!keyData) {
                return {
                    keyNumber: row.value,
                    keyData: undefined,
                    usages: visitDates.map(({date}) => ({
                        type: 'not-issued',
                        date,
                    })),
                };
            } else {
                return {
                    keyNumber: row.value,
                    keyData,
                    usages: mapNotNull(visitDates, ({date}) => {
                        const dateRecords = keyData.history.filter(record => (!record.from || record.from <= date) && (!record.to || record.to >= date));
                        if (dateRecords.length === 0) {
                            return {
                                type: 'no-history',
                                date,
                            };
                        } else if (dateRecords.some(record => record.location.type === 'with-employee' || record.location.type === 'with-guest')) {
                            return null;
                        } else {
                            return {
                                type: 'ghost',
                                locationTypes: dateRecords.map(record => record.location.type) as ('with-vault' | 'lost' | 'damaged' | 'deactivated')[],
                                date,
                            };
                        }
                    }),
                };
            }
        })
        .filter(ghostUsages => ghostUsages.usages.length > 0) as GhostUsage[];
}

function validateKeys(report: KeyOfficeVisitReport, registeredKeys: KeyData[]): KeyValidation {
    const issuedKeys = registeredKeys
        .map<KeyData>(key => ({
                ...key,
                history: key.history.filter(record => (!record.from || record.from <= report.to) && (!record.to || record.to >= report.from))
            })
        )
        .filter(key => key.history.length > 0);

    const issuedKeyNumbers = associateBy(issuedKeys, keyData => keyData.keyNumber);
    const reportedKeyNumbers = new Set(report.sheet.rows.map(row => row.value));

    const missingFromReportKeys = issuedKeys
        .filter(keyData => !reportedKeyNumbers.has(keyData.keyNumber) && keyData.history.some(record => record.location.type !== 'deactivated'));

    const reportedNotIssuedKeys = report.sheet.rows
        .filter(row => !issuedKeyNumbers.has(row.value))
        .map(row => ({
            keyNumber: row.value,
            usages: row.visits.filter(visit => visit).length,
        }));

    return {
        issuedKeys,
        missingFromReportKeys,
        reportedNotIssuedKeys,
        ghostUsages: findGhostUsages(report, issuedKeyNumbers),
    }
}

export const useVisitWithStatStore = create<VisitWithStatStore>()(
    derivedRemoteStore(
        {reports: useVisitReportStore, keyData: useKeyDataStore, desks: useDeskStore},
        async ({reports, keyData, desks}) => {
            const id2report = associateBy(
                reports,
                report => report.id,
                report => report.sheet,
            );
            const id2officeVisits = calculateKeyVisitCountPerDate(id2report);
            const id2cumulative = calculateCumulativeDistribution(id2report);

            return reports
                .map(report => {
                    const officeVisits = id2officeVisits.get(report.id) || [];
                    const deskCount = desks.length;
                    return ({
                        ...report,
                        month: guessMonths(report).join(', '),
                        officeVisits,
                        visitStats: calculateVisitStatistics(officeVisits, deskCount),
                        deskCount,
                        cumulativeVisitDistribution: id2cumulative.get(report.id) || [],
                        validation: validateKeys(report, keyData),
                    });
                })
                .sort(stringCmp('month', true))
        },
    )
);
